/**
 * Google Enhanced Ecommerce Object
 *
 * Event listener logic and functionality for Google Enhanced Ecommerce.
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const events = {

    /**
     * Register Ecommerce Event Handlers
     *
     * @returns  {void}
     */
    initialise: () => {

        if (
            gaEcommerce.events.isInitialised
            || !gaEcommerce.core.checkStorageCapabilities()
        ) return;

        /**
         * Example usage:
         * Add a data attribute to an element to trigger a function.
         * The element clicked is passed to the function triggered.
         *
         * - Attribute name: data-ecom-trigger
         * - Value: name of a gaEcommerce.events function to trigger
         * - Single function: data-ecom-trigger="productClick"
         * - Multiple functions: data-ecom-trigger="productClick, selectPackage"
         * - Functions which can be triggered: productClick, promotionClick, selectPackage
         */
        document.querySelectorAll('[data-ecom-trigger]').forEach((el) => {
            el.addEventListener('click', () => {
                const triggerFunctions = el.dataset.ecomTrigger,
                      whitelist = ['selectPackage', 'productClick', 'promotionClick'];

                // Call functions that exist in the whitelist
                triggerFunctions.split(',')
                    .map(gaEcommerce.util.trim)
                    .filter(gaEcommerce.util.inArrayFilter(whitelist))
                    .map((functionName) => {
                        if (gaEcommerce.events.hasOwnProperty(functionName)) {
                            gaEcommerce.events[functionName](el);
                        }
                    });
            });
        });

        // Handle changes to signup step
        window.addEventListener('gaEcomSignupStepChange', () => {
            gaEcommerce.steps.signupStepChange();
        });

        gaEcommerce.events.isInitialised = true;
    },

    /**
     * Note on fireBasketEvent data:
     * If a change is triggered programmatically you can tell it not to fire a basket event by setting
     * data.fireBasketEvent to false
     * e.g. $(".anInput").data("fireBasketEvent", false).trigger("click");
     *
     * @param {object} basket An "instance" of initialised gaEcommerce.basket
     */
    registerBasketEventHandlers: (basket) => {
        gaEcommerce.events.attachBasketListeners();

        const buttons = document.querySelectorAll(
                basket.broadbandButtonsSelector + ', ' + basket.lineOnlyButtonSelector
            ),
            phoneAndExtrasSteps = document.querySelectorAll(
                basket.phoneStepSelector + ', ' + basket.extrasStepSelector
            ),
            radioButtons = phoneAndExtrasSteps.querySelectorAll(basket.radioButtonsSelector),
            checkboxes = phoneAndExtrasSteps.querySelectorAll(basket.checkboxesSelector);

        buttons.forEach((el) => {
            el.addEventListener('click', (event) => {
                gaEcommerce.events.handleBasketButtonClick(event.target)
            });
        });

        radioButtons.forEach((el) => {
            el.addEventListener('change', (event) => {
                gaEcommerce.events.handleBasketRadioChange(event.target)
            });
        });

        checkboxes.forEach((el) => {
            el.addEventListener('change', (event) => {
                gaEcommerce.events.handleBasketCheckboxChange(event.target)
            });
        });

        window.addEventListener('signupStepChange', () => {
           gaEcommerce.events.handleSignupStepChange();
        });
    },

    /**
     * Set up basket event handlers for account change
     *
     * returns {void}
     */
    registerAccountChangeBasketEventHandlers: () => {
        // Products can be added to the dom dynamically so events need to be delegated
        document.addEventListener('click', (event) => {
            if ('ecomBasketAction' in event.target.dataset) {
                gaEcommerce.accountChangeBasket.handleButton(event.target);
            }
        });

        document.addEventListener('change', (event) => {
            if ('ecomBasketAction' in event.target.dataset) {
                gaEcommerce.accountChangeBasket.handleBasketRadioChange(event.target);
            }
        });
    },

    /**
     * Handles movement between signup steps
     *
     * @returns {void}
     */
    handleSignupStepChange: function () {
        const currentSignupStep     = gaEcommerce.steps.getSignupStep(),
              eventsNotAlreadyFired = !gaEcommerce.basket.optionalExtrasDefaultsFired;

        // Only fire these events once per journey
        if (currentSignupStep === 'optional-extras' && eventsNotAlreadyFired) {

            // Let's delay this scrape until the next tick, so we get the user interaction first
            setTimeout(() => {
                gaEcommerce.basket.scrapeExtrasPage('non-interaction');
            }, 1);

            // Make sure we don't fire it again later
            gaEcommerce.basket.optionalExtrasDefaultsFired = true;
        }
    },

    /**
     * Handles checkbox changes
     *
     * @param {object} selectedCheckbox DOM element representing the selected checkbox
     * @returns {void}
     */
    handleBasketCheckboxChange: (selectedCheckbox) => {
        if (selectedCheckbox.dataset.fireEcomBasketEvent !== 'false') {
            gaEcommerce.basket.handleCheckbox(selectedCheckbox);
        }

        selectedCheckbox.dataset.fireEcomBasketEvent = true;
    },

    /**
     * Handles radio changes
     *
     * @param {object} el DOM element representing the selected radio button
     * @returns {Function}
     */
    handleBasketRadioChange: (selectedRadioButton) => {
        if (gaEcommerce.steps.getSignupStep() !== 'choose-broadband') {
            if (selectedRadioButton.dataset.fireEcomBasketEvent !== 'false') {
                gaEcommerce.basket.handleRadioButtons(
                    selectedRadioButton,
                    // Find all other radio buttons with same name
                    document.querySelectorAll(
                        basket.inputSelector 
                        + '[type="radio"]'
                        + '[name="'+ selectedRadioButton.getAttribute('name') + '"]'
                    )
                );
            }

            thisRadioButton.dataset.fireEcomBasketEvent = true;
        }
    },

    /**
     * Handles basket button clicks
     *
     * @param {object} clickedButton DOM element representing the clicked button
     * @returns {Function}
     */
    handleBasketButtonClick: (clickedButton, buttons) => {
        if (clickedButton.dataset.fireEcomBasketEvent !== 'false') {

            let callback;

            // Is this a broadband button?
            if (clickedButton.closest(basket.broadbandStepSelector)) {

                // Set contract length on line rental to match broadband
                callback = function () {
                    gaEcommerce.basket.updateLineRentalVariant(
                        clickedButton.closest('[data-ecom-product]')?.dataset?.contract
                    );
                }

                gaEcommerce.basket.handleButton(clickedButton, callback, true);
            } else {
                gaEcommerce.basket.handleButton(clickedButton, callback, false);
            }
        }

        clickedButton.dataset.fireEcomBasketEvent = true;
    },

    /**
     * Listen for "real" basket discount changes
     *
     * @returns {void}
     */
    attachBasketListeners: () => {
        window.addEventListener('gaEcomBasketDiscountRemoved', () => {
           gaEcommerce.basket.hasDiscountChanged = true;
        });

        window.addEventListener('gaEcomBasketDiscountAdded', () => {
            gaEcommerce.basket.hasDiscountChanged = true;
        });

        window.addEventListener('gaEcomBasketProcessingFinished', (event) => {
            gaEcommerce.basket.real = event.basket;
            window.dispatchEvent(new CustomEvent('ecommerceRealBasketInitialised'));
        });
    },

    /**
     * selectPackage - Tell the basket which package the user has selected
     *
     * @param  {object}  el  DOM element
     * @returns  {void}
     */
    selectPackage: (el) => {

        // Don't worry about "selectPackage" if we already have an initialised basket
        if (typeof gaEcommerce.basket !== 'undefined' && gaEcommerce.basket.isInitialised) {
            return;
        }

        const productElement = el.closest('[data-ecom-product]');

        if (productElement) {
            gaEcommerce.util.cacheSelection(
                gaEcommerce.util.parseJsonObj(productElement.dataset.ecomProduct)
            );
        }
    },

    /**
     * productClick - Find the product which was clicked, get the data, and send it as a click event to GA.
     *
     * @param  {object}  el  DOM element containing link/button which was clicked.
     * @returns  {void}
     */
    productClick: (el) => {
        const productElement = el.closest('[data-ecom-product]');

        if (productElement) {

            let sanitisedProduct = gaEcommerce.core.validateProduct(
                gaEcommerce.util.parseJsonObj(productElement.dataset.ecomProduct)
            );

            if (sanitisedProduct !== false) {

                // Include any dynamic product properties
                sanitisedProduct = gaEcommerce.core.includeDynamicProductProperties(
                    productElement,
                    sanitisedProduct,
                    { quantity: false}
                );

                const actionField = {};

                if (typeof sanitisedProduct.list !== 'undefined') {
                    actionField.list = sanitisedProduct.list;
                }

                gaEcommerce.core.sendData({
                    click: {
                        actionField: actionField,
                        products: [sanitisedProduct]
                    }
                }, 'productClick');

            }
        }
    },

    /**
     * Calls through to promos.promotionClick
     *
     * @param  {object}  el  DOM element
     *
     * @returns  {void}
     */
    promotionClick: (el) => {
        gaEcommerce.promos.promotionClick(el);
    }
};

export default events;